import React from "react";
import styled from 'styled-components';
import {Table, SearchInput, Filters, FilterGroup} from "../components/tables/table";
import translate from "../providers/i18n/translate";
import { ThemeContext } from "../providers/theme";
import axios from 'axios';
import { useNavigate, Link } from "react-router-dom";
import { useIntl } from 'react-intl';
import { API_URL } from "../config";
import CheckFilter from "../components/tables/table/filters/CheckFilter";
import DateFilter from "../components/tables/table/filters/DateFilter";
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const Root = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 1fr;
    height: 100%;
    width: 100%;
    padding: 0;
    @media (min-width: 1024px) {
        padding: 0;
    }
`

const Content = styled.div`
    position: absolute;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 20px 10px;
    @media (min-width: 1024px) {
        padding: 20px;
    }
`

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  a {
    display: flex;
    align-items: center;
    margin-left: 8px;
  }
`;

export default function RadiographySetReportPage()  {
    const theme = React.useContext(ThemeContext);
    const tableRef = React.useRef();
    const navigate = useNavigate();
    const intl = useIntl();

    const [ search, setSearch ] = React.useState('');

    const columnsTable = [
        { 
            title: translate("name"), 
            field: 'name',
            width: "25%", 
            order: true,  
            hidden: false,
            render: (data) => (
                <LinkContainer>
                    {data.name}
                    <Link to={`/backoffice/horses/${data.id}`} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                    </Link>
                </LinkContainer>
            )
        },
        { title: translate("classification"), field: 'classification',width: "20%", order: true, hidden: false },
        { 
            title: translate("hasRadiographySet"), 
            field: 'hasRadiographySet',
            width: "15%", 
            order: true, 
            align: 'center',
            cellAlign: 'center',
            hidden: false,
            render: (data) => data.hasRadiographySet ? intl.formatMessage({id: 'yes'}) : intl.formatMessage({id: 'no'})
        },
        { 
            title: translate("date"), 
            type: "date", 
            field: 'lastRadiographySetDate',
            width: "15%", 
            order: true, 
            hidden: false,
            render: (data) => data.lastRadiographySetDate ? new Date(data.lastRadiographySetDate).toLocaleDateString() : '-'
        },
        { 
            title: translate("download"), 
            field: 'radiographySetZipUrl',
            width: "5%",
            align: 'center', 
            cellAlign: 'center',
            hidden: false,
            render: (data) => (
                data.radiographySetZipUrl ?
                <a style={{margin: 'auto'}} href={data.radiographySetZipUrl ?? ""} download>
                    <DownloadIcon />
                </a> : null
            )
        }
    ];

    const filters = [
        {
            name: "horse",
            label: intl.formatMessage({id: 'horse'}),
            type: "check",
            src: `${API_URL}/horses/list`
        },
        {
            name: "classification",
            label: intl.formatMessage({id: 'classification'}),
            type: "check",
            src: `${API_URL}/clasificationname`
        },
        {
            name: "lastRadiographySetDate",
            label: intl.formatMessage({id: 'date'}),
            type: "date"
        },
    ];

    const defaultSelectedFilters = [
        {
            name: "horse",
            values: [],
            condition: "equal",
            type: "object",
            search: ''
        },
        {
            name: "classification",
            values: [],
            condition: "equal",
            type: "object"
        },
        {
            name: "lastRadiographySetDate",
            values: [],
            condition: "between",
            type: "date"
        },
    ];

    const [ selectedFilters, setSelectedFilters ] = React.useState(defaultSelectedFilters);

    const onApplyFilter = () => {
        tableRef.current.clean();
    };

    const onClearFilter = () => {
        setSelectedFilters(defaultSelectedFilters);
        tableRef.current.clean();
    };

    const handleCheckFilter = (name, value) => {
        const valueAsInt = parseInt(value, 10);
        const currentFilter = selectedFilters.find(f => f.name === name);
        
        if (!currentFilter) return;

        let newValues;
        if (currentFilter.values.includes(valueAsInt)) {
            newValues = currentFilter.values.filter(val => val !== valueAsInt);
        } else {
            newValues = [...currentFilter.values, valueAsInt];
        }

        const newSelectedFilters = selectedFilters.map(f => 
            f.name === name ? { ...f, values: newValues } : f
        );

        setSelectedFilters(newSelectedFilters);
    };

    const handleFilterSearch = (name, value) => {
        const oldFilters = selectedFilters;
        const newFilters = selectedFilters.filter(f => f.name !== name);
        const filterChange = oldFilters.find(f => f.name === name);
        filterChange["search"] = value;
        newFilters.push(filterChange);
        setSelectedFilters(newFilters);
    }

    const handleDateChange = (name, value) => {
        const oldFilters = selectedFilters;
        const newFilters = selectedFilters.filter(f => f.name !== name);
        const filterChange = oldFilters.find(f => f.name === name);
        filterChange["values"] = value;
        newFilters.push(filterChange);
        setSelectedFilters(newFilters);
    };

    const handleSearch = () => {
        tableRef.current.clean();
    };

    const onSearchChange = (value) => {
        setSearch(value);
    };

    const options = {
        exportButton: true,
        exportName: intl.formatMessage({ id: 'radiography set' }),
        directExport: true,
        pagination: true,
    };

    return (
        <Root>
            <Content>
                <Table
                    disableTotalTitle={true}
                    ref={tableRef}
                    columns={columnsTable}
                    viewTable="list"
                    options={options}
                    data={
                        (query) => new Promise((resolve, reject) => {
                            let filterList = selectedFilters.filter(fil => fil.values.length > 0);
                            axios.get(`${API_URL}/report/radiographySet`,{
                                params: {
                                    page: query.page - 1,
                                    limit: query.pageSize,
                                    search: search,
                                    order: query.order,
                                    filter: JSON.stringify(filterList)
                                }
                            })
                            .then(response => {
                                resolve({
                                    rows: response.data.rows, 
                                    total: response.data.total
                                });
                            })
                            .catch(err => {
                                console.log(err);
                                reject(err);
                            })
                        })
                    }
                    dataExport={
                        (query) => new Promise((resolve, reject) => {
                            let filterList = selectedFilters.filter(fil => fil.values.length > 0);
                            axios.get(`${API_URL}/export/radiographySet`,{
                                params: {
                                    search: search,
                                    order: query.order,
                                    filter: JSON.stringify(filterList)
                                },
                                responseType: 'blob'
                            })
                            .then(response => {
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', query.exportName+'.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                resolve();
                            })
                            .catch(err => {
                                console.log(err);
                                reject(err);
                            })
                        })
                    }
                >
                    <SearchInput search={search} onChange={onSearchChange} onSearch={handleSearch} />
                    <Filters clear={onClearFilter} action={onApplyFilter}>
                        {
                            filters.map((fil, index) =>
                                <FilterGroup onChange={handleFilterSearch} name={fil.name && fil.name} search={fil.name && selectedFilters.find(ft => ft.name === fil.name).search && selectedFilters.find(ft => ft.name === fil.name).search} key={`filter_group_${index}`} title={fil.label}>
                                    {
                                        fil.type === "date" ?
                                        <DateFilter filter={fil} values={selectedFilters.find(ft => ft.name === fil.name).values} onChange={handleDateChange} />
                                        :
                                        <CheckFilter filter={fil} values={selectedFilters.find(ft => ft.name === fil.name).values} handleChange={handleCheckFilter} />
                                    }
                                </FilterGroup>
                            )
                        }
                    </Filters>
                </Table>
            </Content>
        </Root>
    )
} 